import React from 'react';
import { AlertCircle, CheckCircle2, Clock, FileCheck2, ShieldCheck, Upload, XCircle } from 'lucide-react';

/**
 * Deriving the next stage from the deal + FCO + checklist state.
 *
 * Progression: no-FCO → FCO issued → FCO acknowledged → POP → POF → SPA → done.
 * The banner is role-aware: sellers, buyers and admins each see the action
 * that unblocks *them*. If the deal is closed / cancelled, no banner renders.
 */
export default function NextStageBanner({ deal, fco, checklist, viewerRole }) {
  const stage = deriveNextStage({ deal, fco, checklist, viewerRole });
  if (!stage) return null;

  const { title, tip, tone, Icon, testid } = stage;
  const toneClasses = {
    info:    'border-[#F59E0B]/40 bg-[#F59E0B]/[0.08] text-[#F59E0B]',
    success: 'border-[#10B981]/40 bg-[#10B981]/[0.08] text-[#10B981]',
    warn:    'border-[#EF4444]/40 bg-[#EF4444]/[0.08] text-[#EF4444]',
    idle:    'border-[#1E293B] bg-white/[0.04] text-[#94A3B8]',
  }[tone];

  return (
    <div className={`rounded-lg border ${toneClasses} px-4 py-3 mb-5 flex items-start gap-3`}
         data-testid="next-stage-banner"
         data-branch={testid || 'default'}>
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="font-semibold text-sm" data-testid="next-stage-title">{title}</p>
        <p className="text-xs mt-0.5 text-[#94A3B8]" data-testid="next-stage-tip">{tip}</p>
      </div>
    </div>
  );
}

/**
 * Pure function so it's cheap to unit-test. Returns null when there's
 * nothing actionable (deal cancelled, all steps done, etc.).
 */
export function deriveNextStage({ deal, fco, checklist, viewerRole }) {
  if (!deal) return null;
  if (['cancelled', 'closed', 'completed'].includes(deal.status)) return null;

  const forSeller = viewerRole === 'seller';
  const forBuyer  = viewerRole === 'buyer';
  const forAdmin  = viewerRole === 'admin';

  // 1. No FCO yet.
  if (!fco) {
    if (deal.status !== 'in_progress') {
      return {
        title: 'Deal not yet in progress',
        tip: 'The FCO stage unlocks once the deal advances to In Progress.',
        tone: 'idle', Icon: Clock, testid: 'next-stage-idle',
      };
    }
    if (forSeller) return {
      title: 'Next up · Issue the Full Corporate Offer (FCO)',
      tip: 'Click "Generate FCO" below and fill in commodity specs, quantity, price and incoterm.',
      tone: 'info', Icon: FileCheck2, testid: 'next-stage-issue-fco',
    };
    return {
      title: 'Waiting on seller · FCO not yet issued',
      tip: 'The seller has been notified and will send a Full Corporate Offer shortly.',
      tone: 'idle', Icon: Clock, testid: 'next-stage-await-fco',
    };
  }

  // 2. FCO issued — buyer needs to act.
  if (fco.status === 'issued') {
    if (fco.is_expired) return {
      title: 'FCO expired · Ask the seller to re-issue',
      tip: forSeller
        ? 'Click "Supersede with New FCO" to send an updated offer to the buyer.'
        : 'Contact the seller through this deal to request a refreshed FCO with new dates.',
      tone: 'warn', Icon: AlertCircle, testid: 'next-stage-fco-expired',
    };
    if (forBuyer) return {
      title: 'Next up · Acknowledge or reject the FCO',
      tip: 'Review the commodity specs and commercial terms below, then click Acknowledge (or Reject with a reason).',
      tone: 'info', Icon: FileCheck2, testid: 'next-stage-ack-fco',
    };
    return {
      title: 'Waiting on buyer · Acknowledgement pending',
      tip: 'The buyer has been notified. They have until the countdown expires to acknowledge.',
      tone: 'idle', Icon: Clock, testid: 'next-stage-await-ack',
    };
  }

  if (fco.status === 'rejected') return {
    title: 'FCO rejected · A revised version is expected',
    tip: forSeller
      ? 'Click "Issue Revised FCO" and address the buyer\'s rejection reason before resubmitting.'
      : 'You have rejected the FCO. Waiting for the seller to issue a revised version.',
    tone: 'warn', Icon: XCircle, testid: 'next-stage-fco-rejected',
  };

  // 3. FCO acknowledged → checklist drives the rest.
  if (fco.status === 'acknowledged') {
    const steps = checklist?.steps || {};
    const pop = steps.proof_of_product || { status: 'pending' };
    const pof = steps.proof_of_funds  || { status: 'pending' };
    const spa = steps.spa_execution   || { status: 'pending' };

    // Proof of Product (seller) first.
    if (pop.status === 'pending' || pop.status === 'rejected') {
      const wasRejected = pop.status === 'rejected';
      if (forSeller) return {
        title: wasRejected
          ? 'Re-upload Proof of Product'
          : 'Next up · Upload Proof of Product (POP)',
        tip: wasRejected
          ? `Admin flagged the last upload: "${pop.rejection_reason || 'see notes'}". Upload a corrected PDF or photo below.`
          : 'Attach a warehouse receipt, mill certificate or inventory photo (PDF / JPG / PNG · max 8 MB).',
        tone: wasRejected ? 'warn' : 'info',
        Icon: wasRejected ? XCircle : Upload,
        testid: 'next-stage-pop-upload',
      };
      return {
        title: 'Waiting on seller · Proof of Product upload',
        tip: 'The seller must attach evidence the goods are held (warehouse receipt, mill cert or inventory photo).',
        tone: 'idle', Icon: Clock, testid: 'next-stage-await-pop',
      };
    }
    if (pop.status === 'uploaded') {
      if (forAdmin) return {
        title: 'Admin action · Review Proof of Product',
        tip: 'Open the Checklists tab or scroll below to Verify or Reject the seller\'s POP upload.',
        tone: 'info', Icon: ShieldCheck, testid: 'next-stage-pop-review',
      };
      return {
        title: 'POP uploaded · Awaiting admin verification',
        tip: 'GlobalMinerX will review the Proof of Product document within one business day.',
        tone: 'idle', Icon: Clock, testid: 'next-stage-pop-pending-verify',
      };
    }

    // Proof of Funds (buyer) next.
    if (pof.status === 'pending' || pof.status === 'rejected') {
      const wasRejected = pof.status === 'rejected';
      if (forBuyer) return {
        title: wasRejected
          ? 'Re-upload Proof of Funds'
          : 'Next up · Upload Proof of Funds (POF)',
        tip: wasRejected
          ? `Admin flagged the last upload: "${pof.rejection_reason || 'see notes'}". Upload a corrected bank letter or statement.`
          : 'Attach a recent (< 30 days) bank comfort letter, MT799 or statement covering the deal value (PDF / JPG · max 8 MB).',
        tone: wasRejected ? 'warn' : 'info',
        Icon: wasRejected ? XCircle : Upload,
        testid: 'next-stage-pof-upload',
      };
      return {
        title: 'Waiting on buyer · Proof of Funds upload',
        tip: 'The buyer must attach a bank comfort letter or MT799 covering the deal value.',
        tone: 'idle', Icon: Clock, testid: 'next-stage-await-pof',
      };
    }
    if (pof.status === 'uploaded') {
      if (forAdmin) return {
        title: 'Admin action · Review Proof of Funds',
        tip: 'Open the Checklists tab to Verify or Reject the buyer\'s POF upload.',
        tone: 'info', Icon: ShieldCheck, testid: 'next-stage-pof-review',
      };
      return {
        title: 'POF uploaded · Awaiting admin verification',
        tip: 'GlobalMinerX will review the Proof of Funds document within one business day.',
        tone: 'idle', Icon: Clock, testid: 'next-stage-pof-pending-verify',
      };
    }

    // SPA — both POP + POF verified.
    if (spa.status === 'pending') {
      if (forSeller) return {
        title: 'Next up · Generate the SPA',
        tip: 'From the deal actions below, click "Generate SPA" to draft the Sales & Purchase Agreement.',
        tone: 'info', Icon: FileCheck2, testid: 'next-stage-spa-generate',
      };
      return {
        title: 'Waiting on seller · SPA generation',
        tip: 'Both proofs are verified. The seller will now generate the Sales & Purchase Agreement.',
        tone: 'idle', Icon: Clock, testid: 'next-stage-await-spa',
      };
    }
    if (spa.status === 'in_progress') return {
      title: 'Next up · Sign the SPA',
      tip: forSeller || forBuyer
        ? 'Open the SPA below, review it and click "Sign SPA" to add your signature.'
        : 'Both parties are signing the Sales & Purchase Agreement.',
      tone: 'info', Icon: Upload, testid: 'next-stage-sign-spa',
    };
    if (spa.status === 'verified') return {
      title: 'All steps complete · Ready for payment',
      tip: 'POP, POF and SPA are all cleared. The deal will proceed to payment and shipment.',
      tone: 'success', Icon: CheckCircle2, testid: 'next-stage-complete',
    };
  }

  return null;
}
